'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { Menu, X, ChevronDown } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { LogoMark } from './Logo'
import { isAuthenticated, getStoredUser, clearAuth } from '@/lib/auth'
import type { User } from '@/types'

// Mirrors the focusable selector used by <Modal> for its focus trap.
const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

// Signed-in account dropdown for the marketing nav (desktop). Marketing
// pages are statically served (Cloudflare Pages) so auth is detected on
// the CLIENT after mount (see MarketingNav's effect). This menu replaces
// the "Sign in / Sign up" CTA once a session is detected: the user's name
// + Dashboard / Settings / Sign out. Self-contained: owns its open state,
// closes on Escape / outside-click / route change, keyboard- + SR-accessible.
function AccountMenu({
  user,
  labels,
  onSignOut,
}: {
  user: User
  labels: { dashboard: string; settings: string; signOut: string; account: string }
  onSignOut: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const pathname = usePathname()

  // First name keeps the button compact; fall back to the email local-part
  // then a generic label so the button always has text.
  const displayName =
    (user.full_name || '').trim().split(/\s+/)[0] ||
    (user.email || '').split('@')[0] ||
    labels.account

  // Close on route change so the menu never lingers after navigating.
  useEffect(() => {
    setOpen(false)
  }, [pathname])

  // Close on Escape + outside-click while open.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onClick)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onClick)
    }
  }, [open])

  const itemClass =
    'block px-4 py-2.5 text-[15px] text-ink-900 hover:bg-cream-200/70 focus-visible:bg-cream-200/70 focus-visible:outline-none transition-colors'

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={labels.account}
        className="flex items-center gap-1.5 text-[15px] font-medium text-ink-900 px-3 py-2 rounded-lg hover:text-terra-500 hover:bg-ink-900/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terra-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-100 transition-colors"
      >
        <span className="max-w-[140px] truncate">{displayName}</span>
        <ChevronDown
          className={'w-4 h-4 transition-transform duration-200 ' + (open ? 'rotate-180' : '')}
          aria-hidden
        />
      </button>
      {open && (
        <div
          role="menu"
          aria-label={labels.account}
          className="absolute right-0 mt-2 w-52 rounded-xl border border-ink-900/10 bg-cream-100/95 backdrop-blur-xl shadow-lg overflow-hidden py-1"
        >
          <Link href="/dashboard" role="menuitem" className={itemClass}>
            {labels.dashboard}
          </Link>
          <Link href="/dashboard/settings" role="menuitem" className={itemClass}>
            {labels.settings}
          </Link>
          <div className="rule my-1" />
          <button
            type="button"
            role="menuitem"
            onClick={onSignOut}
            className={itemClass + ' w-full text-left text-terra-600'}
          >
            {labels.signOut}
          </button>
        </div>
      )}
    </div>
  )
}

// "Alternatives" is deliberately NOT in the top nav: putting alternatives
// on the homepage banner reads as defensive (leading with a comparison
// to a competitor instead of our own value). The pages still exist and
// rank for "{competitor} alternative" SEO queries, but they're discovered
// via the footer + via Google, not from the marketing hero. See
// Footer.tsx for the discoverable link.
// v77: /about inlined here (no dropdown infra exists and adding one is more
// risk than an extra link). The remaining 2 v77 pages (/privacy/dpa,
// /privacy/aup) are footer-only. Legal boilerplate is never primary-nav
// discoverable.
// v100: /changelog de-linked from the public nav (owner decision; kept as an
// internal surface, not client-facing). The page still exists and is reachable
// directly; it is just no longer surfaced in the marketing nav.
// v112: the dedicated /modules page is killed (the homepage tells the modules
// story once), so the 'modules' nav entry is removed. 'security' is moved out
// of the top nav into the footer; it is not an attention-worthy primary link.
const NAV_LINK_KEYS = [
  { key: 'pricing',   href: '/#pricing' },
  { key: 'faqs',      href: '/#faq' },
  { key: 'blogs',     href: '/blog' },
  { key: 'about',     href: '/about' },
  { key: 'dashboard', href: '/dashboard' },
] as const

export default function MarketingNav() {
  const t = useTranslations('Nav')
  const pathname = usePathname()
  const router = useRouter()
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)
  // Focus management for the mobile drawer (role="dialog" aria-modal).
  const mobilePanelRef = useRef<HTMLDivElement>(null)
  const burgerRef = useRef<HTMLButtonElement>(null)
  // Client-side auth detection. Marketing pages are statically served, so
  // we can only read the session after mount. `user === null` is the
  // signed-out default (also the SSR/first-paint state, so there is no
  // hydration mismatch); the effect swaps it in once localStorage is read.
  const [user, setUser] = useState<User | null>(null)

  useEffect(() => {
    const sync = () => setUser(isAuthenticated() ? getStoredUser() : null)
    sync()
    // saveAuth/clearAuth broadcast this on login/logout from any surface,
    // so the marketing nav reacts without a page reload.
    window.addEventListener('ps:auth-change', sync)
    return () => window.removeEventListener('ps:auth-change', sync)
  }, [])

  const onSignOut = () => {
    clearAuth()
    setUser(null)
    setOpen(false)
    router.push('/')
  }

  // Dashboard is a permanent top-nav quick link (sits alongside pricing /
  // faqs / about) in BOTH the signed-out and signed-in states, so the route
  // is always one click away from the marketing surface. When signed in the
  // account dropdown ALSO carries a Dashboard entry; the persistent bar link
  // is the primary affordance and the dropdown copy is the in-menu shortcut.
  const navLinks = NAV_LINK_KEYS
  const accountLabels = {
    dashboard: t('dashboard'),
    settings: t('settings'),
    signOut: t('signOut'),
    account: t('account'),
  }

  // NB-F323: a link counts as "current" when the pathname matches its
  // route exactly (e.g. /about) or starts with that route plus a slash
  // (so /blog/some-post highlights /blog). Anchor links (/#pricing) are
  // skipped. The homepage handles their own scroll-spy.
  const isCurrent = (href: string): boolean => {
    if (href.includes('#')) return false
    if (href === '/') return pathname === '/'
    return pathname === href || pathname.startsWith(href + '/')
  }

  // Mobile drawer (role="dialog" aria-modal): Escape-close, body-scroll lock,
  // and full focus management. Previously the drawer had aria-modal="true" but
  // ZERO focus handling (M-MARKETING-MOBILENAV-NO-FOCUSTRAP) — focus stayed on
  // the page behind it, Tab walked into content the user was told was inert,
  // and focus wasn't restored to the burger on close. This mirrors <Modal>'s
  // trap: move focus into the panel on open, cycle Tab/Shift+Tab within it,
  // and restore focus to the burger on close.
  useEffect(() => {
    if (!open) return
    const previouslyFocused = document.activeElement as HTMLElement | null
    // Capture the burger node now so the cleanup restores focus to the node
    // that existed while the drawer was open (avoids the stale-ref warning).
    const burgerAtOpen = burgerRef.current

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setOpen(false)
        return
      }
      if (e.key !== 'Tab') return
      const panel = mobilePanelRef.current
      if (!panel) return
      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE),
      )
      if (focusables.length === 0) {
        e.preventDefault()
        panel.focus()
        return
      }
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement as HTMLElement | null
      if (e.shiftKey && (active === first || !panel.contains(active))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
        e.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Move focus into the drawer (first link, or the panel itself).
    const panel = mobilePanelRef.current
    const firstFocusable = panel?.querySelector<HTMLElement>(FOCUSABLE)
    ;(firstFocusable ?? panel)?.focus()

    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
      // Restore focus to the burger (or wherever it was) on close.
      const restore = burgerAtOpen ?? previouslyFocused
      if (restore && typeof restore.focus === 'function') restore.focus()
    }
  }, [open])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header
      role="banner"
      // Safari fix: `bg-transparent` + `fixed` defers Safari's compositor
      // promotion until the first scroll repaint, which made nav links feel
      // unclickable on first paint until the user nudged the page. Forcing a
      // translateZ(0) promotes the layer at mount so hit-testing is live
      // immediately. Also constrains the transition to the properties that
      // actually change (was `transition-all`, which fights the compositor).
      style={{ transform: 'translateZ(0)', willChange: 'background-color, backdrop-filter, border-color' }}
      className={
        'fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-300 ' +
        (scrolled
          ? 'bg-cream-100/85 backdrop-blur-xl border-b border-ink-900/10'
          : 'bg-cream-100/0 backdrop-blur-0 border-b border-transparent')
      }
    >
      <div className="editorial flex items-center h-[72px] sm:h-20 gap-6">
        {/* Logo - left */}
        <Link
          href="/"
          aria-label="PulseSignal home"
          aria-current={pathname === '/' ? 'page' : undefined}
          className="flex items-center gap-2.5 group shrink-0 transition-transform duration-200 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terra-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-100 rounded-lg"
        >
          <LogoMark size={48} />
          <span className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-ink-900 group-hover:text-terra-500 transition-colors duration-200">
            PulseSignal
          </span>
        </Link>

        {/* Nav - center, evenly spread */}
        {/* NB-F323: aria-label gives this region a name so SR users can
            jump to it ("Primary"); aria-current="page" highlights the
            active route so visual + AT navigation agree on where we are. */}
        <nav
          aria-label="Primary"
          className="hidden md:flex items-center gap-6 lg:gap-8 mx-auto"
        >
          {navLinks.map((l) => {
            const current = isCurrent(l.href)
            return (
              <Link
                key={l.href}
                href={l.href}
                aria-current={current ? 'page' : undefined}
                className={
                  'link-nav text-[16px] font-medium transition-colors duration-200 ' +
                  'focus-visible:outline-none focus-visible:text-terra-600 focus-visible:underline underline-offset-[6px] ' +
                  (current
                    ? 'text-ink-900 underline decoration-terra-500 decoration-2 underline-offset-[6px]'
                    : 'text-ink-700 hover:text-ink-900')
                }
              >
                {t(l.key)}
              </Link>
            )
          })}
        </nav>

        {/* CTA - right. Signed-out: the Sign in / Sign up button. Signed-in
            (detected client-side after mount): the account dropdown. */}
        <div className="hidden md:flex items-center shrink-0 ml-auto md:ml-0">
          {user ? (
            <AccountMenu user={user} labels={accountLabels} onSignOut={onSignOut} />
          ) : (
            <Link
              href="/login"
              className="btn-primary text-[15px] px-5 py-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terra-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-100"
            >
              {t('signInSignUp')}
            </Link>
          )}
        </div>

        {/* Mobile burger */}
        <button
          ref={burgerRef}
          className="md:hidden text-ink-900 p-3 -mr-2 ml-auto rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terra-500/60"
          onClick={() => setOpen(!open)}
          aria-label={t('toggleMenu')}
          aria-expanded={open}
          aria-controls="mobile-nav"
        >
          {open ? <X className="w-6 h-6" aria-hidden /> : <Menu className="w-6 h-6" aria-hidden />}
        </button>
      </div>

      {open && (
        <div
          id="mobile-nav"
          ref={mobilePanelRef}
          tabIndex={-1}
          // Compositor-lag workaround for the mobile burger drawer's
          // menu items (Safari). `motion-safe:animate-…` lets users
          // with prefers-reduced-motion: reduce skip the slide.
          style={{ transform: 'translateZ(0)', willChange: 'transform, backdrop-filter' }}
          className="md:hidden border-t border-ink-900/10 bg-cream-100/95 backdrop-blur-xl motion-safe:animate-[slideDown_180ms_ease-out] focus:outline-none"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
        >
          <nav aria-label="Mobile primary" className="editorial py-5 flex flex-col gap-4">
            {navLinks.map((l) => {
              const current = isCurrent(l.href)
              return (
                <Link
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  aria-current={current ? 'page' : undefined}
                  className={
                    'text-lg py-1 transition-colors focus-visible:outline-none focus-visible:text-terra-600 ' +
                    (current
                      ? 'text-terra-500 font-semibold'
                      : 'text-ink-900 hover:text-terra-500 active:text-terra-600')
                  }
                >
                  {t(l.key)}
                </Link>
              )
            })}
            <div className="rule mt-2" />
            {user ? (
              <>
                <Link
                  href="/dashboard"
                  onClick={() => setOpen(false)}
                  className="text-lg py-1 text-ink-900 hover:text-terra-500 transition-colors focus-visible:outline-none focus-visible:text-terra-600"
                >
                  {t('dashboard')}
                </Link>
                <Link
                  href="/dashboard/settings"
                  onClick={() => setOpen(false)}
                  className="text-lg py-1 text-ink-900 hover:text-terra-500 transition-colors focus-visible:outline-none focus-visible:text-terra-600"
                >
                  {t('settings')}
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false)
                    onSignOut()
                  }}
                  className="text-lg py-1 text-left text-terra-600 hover:text-terra-700 transition-colors focus-visible:outline-none"
                >
                  {t('signOut')}
                </button>
              </>
            ) : (
              <Link
                href="/login"
                onClick={() => setOpen(false)}
                className="btn-primary justify-center"
              >
                {t('signInSignUp')}
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  )
}
